import React, { useEffect, useMemo } from 'react';

// 5-minute increments (00, 05, 10 ... 55) - agents stagger open homes on the
// 5s (e.g. 9:40 / 9:45 / 9:50), so quarter-hours alone were too coarse.
export const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

function nearestMinuteOption(min) {
  const m = Math.max(0, Math.min(59, parseInt(min, 10) || 0));
  return MINUTE_OPTIONS.reduce(
    (best, q) => (Math.abs(parseInt(q, 10) - m) < Math.abs(parseInt(best, 10) - m) ? q : best),
    MINUTE_OPTIONS[0]
  );
}

export function parseTime24(value) {
  if (!value || typeof value !== 'string' || !value.includes(':')) {
    return { hour: '08', minute: '00', period: 'PM' };
  }
  const [hStr, mStr] = value.split(':');
  let h24 = parseInt(hStr, 10);
  if (Number.isNaN(h24)) h24 = 20;
  const minute = nearestMinuteOption(mStr);
  const period = h24 >= 12 ? 'PM' : 'AM';
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return {
    hour: String(h12).padStart(2, '0'),
    minute,
    period,
  };
}

export function toTime24(hour12, minute, period) {
  let h = parseInt(hour12, 10) || 12;
  if (period === 'AM') {
    if (h === 12) h = 0;
  } else if (h !== 12) {
    h += 12;
  }
  return `${String(h).padStart(2, '0')}:${minute}`;
}

/**
 * Time picker with 5-minute increments (00, 05, 10 ... 55).
 * value / onChange use 24h "HH:mm" to match existing inspection forms.
 */
export default function TimeInput({ value, onChange, className = '', required, focusRingClass = 'focus:ring-[#55C5D0]' }) {
  const { hour, minute, period } = useMemo(() => parseTime24(value), [value]);

  useEffect(() => {
    if (!onChange) return;
    const normalized = toTime24(hour, minute, period);
    // Sync displayed defaults into parent state when value is empty, and
    // normalize partial/invalid values (e.g. minutes off the 5-minute grid).
    if (!value || normalized !== value) {
      onChange({ target: { value: normalized } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-normalize when value changes
  }, [value]);

  const emit = (h, m, p) => {
    onChange?.({ target: { value: toTime24(h, m, p) } });
  };

  const selectClass = `flex-1 min-w-0 pl-2 pr-1 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 ${focusRingClass}`;

  return (
    <div className={`flex gap-2 ${className}`}>
      <select
        value={hour}
        onChange={(e) => emit(e.target.value, minute, period)}
        required={required}
        className={selectClass}
        aria-label="Hour"
      >
        {HOURS_12.map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <select
        value={minute}
        onChange={(e) => emit(hour, e.target.value, period)}
        required={required}
        className={selectClass}
        aria-label="Minute"
      >
        {MINUTE_OPTIONS.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
      <select
        value={period}
        onChange={(e) => emit(hour, minute, e.target.value)}
        required={required}
        className={selectClass}
        aria-label="AM or PM"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}
